import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { StatCard } from '../components/StatCard';
import { Badge } from '../components/Badge';
import { ModuleCard } from '../components/ModuleCard';
import { formatCents, formatDate } from '../utils/format';
import { useFlowIAScreenContext } from '../hooks/useFlowIAScreenContext';
import { DEMO_NOTIFICATIONS, DEMO_RRHH_TASKS } from '../data/commsNotificationsDemo';
import { isEmployeeHubPersona } from '../utils/rrhhPersona';

const DEFAULT_SUMMARY = {
  total_employees: 0,
  active_employees: 0,
  new_hires_month: 0,
  pending_leaves: 0,
  pending_expenses: 0,
  unpaid_expenses: 0,
  average_climate_score: 0,
  employee_of_month: { id: '', name: '—', area: '', avatar_url: null },
  documents_total: 0,
  documents_pending: 0,
  documents_expired: 0,
  documents_approved: 0,
  compliance_rate: 100,
  attendance_present_today: 0,
  communications_published: 0,
  people_intelligence_score: 0,
  pending_expense_amount_cents: 0,
  documents_missing_file: 0,
  payslips_available: 0,
  payslips_pending_signature: 0,
  payslips_draft: 0,
  payslips_signed: 0,
  payslips_observed: 0,
  storage_status: 'metadata_only',
};

function normalizeSummary(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SUMMARY };
  const eom = raw.employee_of_month && typeof raw.employee_of_month === 'object' ? raw.employee_of_month : {};
  return {
    ...DEFAULT_SUMMARY,
    ...raw,
    average_climate_score:
      typeof raw.average_climate_score === 'number' && Number.isFinite(raw.average_climate_score)
        ? raw.average_climate_score
        : 0,
    documents_total: typeof raw.documents_total === 'number' ? raw.documents_total : DEFAULT_SUMMARY.documents_total,
    documents_pending: typeof raw.documents_pending === 'number' ? raw.documents_pending : DEFAULT_SUMMARY.documents_pending,
    documents_expired: typeof raw.documents_expired === 'number' ? raw.documents_expired : DEFAULT_SUMMARY.documents_expired,
    documents_approved: typeof raw.documents_approved === 'number' ? raw.documents_approved : DEFAULT_SUMMARY.documents_approved,
    compliance_rate:
      typeof raw.compliance_rate === 'number' && Number.isFinite(raw.compliance_rate)
        ? raw.compliance_rate
        : DEFAULT_SUMMARY.compliance_rate,
    total_employees: typeof raw.total_employees === 'number' ? raw.total_employees : DEFAULT_SUMMARY.total_employees,
    attendance_present_today:
      typeof raw.attendance_present_today === 'number' ? raw.attendance_present_today : DEFAULT_SUMMARY.attendance_present_today,
    communications_published:
      typeof raw.communications_published === 'number' ? raw.communications_published : DEFAULT_SUMMARY.communications_published,
    people_intelligence_score:
      typeof raw.people_intelligence_score === 'number' ? raw.people_intelligence_score : DEFAULT_SUMMARY.people_intelligence_score,
    pending_expense_amount_cents:
      typeof raw.pending_expense_amount_cents === 'number'
        ? raw.pending_expense_amount_cents
        : DEFAULT_SUMMARY.pending_expense_amount_cents,
    documents_missing_file:
      typeof raw.documents_missing_file === 'number' ? raw.documents_missing_file : DEFAULT_SUMMARY.documents_missing_file,
    payslips_available:
      typeof raw.payslips_available === 'number' ? raw.payslips_available : DEFAULT_SUMMARY.payslips_available,
    payslips_pending_signature:
      typeof raw.payslips_pending_signature === 'number'
        ? raw.payslips_pending_signature
        : DEFAULT_SUMMARY.payslips_pending_signature,
    payslips_draft: typeof raw.payslips_draft === 'number' ? raw.payslips_draft : DEFAULT_SUMMARY.payslips_draft,
    payslips_signed: typeof raw.payslips_signed === 'number' ? raw.payslips_signed : DEFAULT_SUMMARY.payslips_signed,
    payslips_observed: typeof raw.payslips_observed === 'number' ? raw.payslips_observed : DEFAULT_SUMMARY.payslips_observed,
    storage_status: typeof raw.storage_status === 'string' ? raw.storage_status : DEFAULT_SUMMARY.storage_status,
    employee_of_month: {
      ...DEFAULT_SUMMARY.employee_of_month,
      ...eom,
      name: eom.name ?? DEFAULT_SUMMARY.employee_of_month.name,
    },
  };
}

const STRATEGIC_MODULES = [
  { to: '/records', titleKey: 'dashboard.strategicCardLegajos', descKey: 'dashboard.strategicCardLegajosDesc', icon: '▣', status: 'active' },
  { to: '/employee-portal', titleKey: 'dashboard.strategicCardPortal', descKey: 'dashboard.strategicCardPortalDesc', icon: '◇', status: 'active' },
  { to: '/payroll-receipts', titleKey: 'dashboard.strategicCardRecibos', descKey: 'dashboard.strategicCardRecibosDesc', icon: '▤', status: 'soon' },
  { to: '/people-intelligence', titleKey: 'dashboard.strategicCardPI', descKey: 'dashboard.strategicCardPIDesc', icon: '◉', status: 'active' },
  { to: '/personal-finance', titleKey: 'dashboard.strategicCardWellbeing', descKey: 'dashboard.strategicCardWellbeingDesc', icon: '◎', status: 'active' },
  { to: '/expense-reports', titleKey: 'dashboard.strategicCardFinance', descKey: 'dashboard.strategicCardFinanceDesc', icon: '⧗', status: 'prepared' },
  { to: '/reports', titleKey: 'dashboard.strategicCardReports', descKey: 'dashboard.strategicCardReportsDesc', icon: '▥', status: 'active' },
];

const OPS_HR_STATUS = [
  { to: '/records', labelKey: 'dashboard.opsLegajo' },
  { to: '/attendance', labelKey: 'dashboard.opsAttendance' },
  { to: '/leaves', labelKey: 'dashboard.opsLeaves' },
  { to: '/expense-reports', labelKey: 'dashboard.opsExpenses' },
  { to: '/communications', labelKey: 'dashboard.opsComms' },
  { to: '/performance', labelKey: 'dashboard.opsEval' },
  { to: '/training', labelKey: 'dashboard.opsTraining' },
  { to: '/surveys', labelKey: 'dashboard.opsClimate' },
  { to: '/reports', labelKey: 'dashboard.opsReports' },
];

const DEMO_READY_KEYS = ['demoCk1', 'demoCk2', 'demoCk3', 'demoCk4', 'demoCk5', 'demoCk6', 'demoCk7'];
const NEXT_REAL_KEYS = ['nextReal1', 'nextReal2', 'nextReal3', 'nextReal4', 'nextReal5', 'nextReal6', 'nextReal7'];

function expenseMetrics(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const pendingApproval = list.filter((x) => ['submitted', 'manager_approved'].includes(x.status)).length;
  const unpaid = list.filter((x) => !['paid', 'cancelled', 'rejected'].includes(x.status)).length;
  return { pendingApproval, unpaid };
}

function riskTone(level) {
  if (level === 'high') return 'dash-pi-risk--high';
  if (level === 'medium') return 'dash-pi-risk--mid';
  return 'dash-pi-risk--low';
}

function hrDocBadgeVariant(status) {
  const m = {
    pending: 'hr_pending',
    uploaded: 'hr_uploaded',
    approved: 'hr_approved',
    rejected: 'hr_rejected',
    expired: 'hr_expired',
  };
  return m[status] || 'hr_pending';
}

export function Dashboard() {
  const { t, permSummary, locale, isProductionUi, authMe } = useApp();
  const [summary, setSummary] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [awards, setAwards] = useState([]);
  const [financeScore, setFinanceScore] = useState(null);
  const [piSnapshot, setPiSnapshot] = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);
  const [recentComms, setRecentComms] = useState([]);
  const [rrhhEmailLogs, setRrhhEmailLogs] = useState([]);
  const [rbacCatalog, setRbacCatalog] = useState(null);
  const [pubMail, setPubMail] = useState(null);
  const [pubStorage, setPubStorage] = useState(null);

  useEffect(() => {
    api
      .get('/api/rrhh-emails/config')
      .then((r) => setPubMail(r.data))
      .catch(() => setPubMail(null));
    api
      .get('/api/storage/config')
      .then((r) => setPubStorage(r.data))
      .catch(() => setPubStorage(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const settled = await Promise.allSettled([
        api.get('/api/dashboard/summary'),
        api.get('/api/leaves'),
        api.get('/api/expense-reports'),
        api.get('/api/awards'),
        api.get('/api/personal-finance/summary'),
        api.get('/api/people-intelligence/summary'),
        api.get('/api/hr-documents'),
        api.get('/api/communications'),
        api.get('/api/rrhh-emails/logs', { params: { limit: 8 } }),
        api.get('/api/roles/catalog'),
      ]);
      if (cancelled) return;

      const [sRes, lRes, eRes, aRes, pfRes, piRes, docRes, commRes, mailRes, catalogRes] = settled;

      if (sRes.status === 'fulfilled') {
        setSummary(normalizeSummary(sRes.value.data));
      } else {
        console.error('Dashboard load error', sRes.reason);
        setSummary(normalizeSummary(null));
      }

      if (lRes.status === 'fulfilled') {
        const list = Array.isArray(lRes.value.data) ? lRes.value.data : [];
        const sortedLeaves = [...list].sort((x, y) => new Date(x.start_date) - new Date(y.start_date));
        setLeaves(sortedLeaves.slice(0, 5));
      } else {
        setLeaves([]);
      }

      if (eRes.status === 'fulfilled') {
        const list = Array.isArray(eRes.value.data) ? eRes.value.data : [];
        const sortedExp = [...list].sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
        setAllExpenses(sortedExp);
      } else {
        setAllExpenses([]);
      }

      if (aRes.status === 'fulfilled') {
        const list = Array.isArray(aRes.value.data) ? aRes.value.data : [];
        setAwards(list.slice(0, 5));
      } else {
        setAwards([]);
      }

      if (pfRes.status === 'fulfilled' && pfRes.value.data && typeof pfRes.value.data.financial_health_score === 'number') {
        setFinanceScore(pfRes.value.data.financial_health_score);
      } else {
        setFinanceScore(null);
      }

      if (piRes.status === 'fulfilled' && piRes.value.data) {
        setPiSnapshot(piRes.value.data);
      } else {
        setPiSnapshot(null);
      }

      if (docRes.status === 'fulfilled' && Array.isArray(docRes.value.data)) {
        const sorted = [...docRes.value.data].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        setRecentDocs(sorted.slice(0, 4));
      } else {
        setRecentDocs([]);
      }

      if (commRes.status === 'fulfilled' && Array.isArray(commRes.value.data)) {
        const pub = commRes.value.data.filter((c) => c.status === 'published');
        setRecentComms(pub.slice(0, 4));
      } else {
        setRecentComms([]);
      }

      if (mailRes.status === 'fulfilled' && Array.isArray(mailRes.value.data)) {
        setRrhhEmailLogs(mailRes.value.data.slice(0, 5));
      } else {
        setRrhhEmailLogs([]);
      }

      if (catalogRes.status === 'fulfilled' && catalogRes.value.data && typeof catalogRes.value.data === 'object') {
        setRbacCatalog(catalogRes.value.data);
      } else {
        setRbacCatalog(null);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const { pendingApproval } = useMemo(() => expenseMetrics(allExpenses), [allExpenses]);
  const recentExpenses = useMemo(() => allExpenses.slice(0, 4), [allExpenses]);

  const peopleOpsStats = useMemo(() => {
    if (!summary) return null;
    const docLane = (Number(summary.documents_pending) || 0) + (Number(summary.documents_expired) || 0);
    return {
      onboard: 1,
      offboard: 2,
      hrTasks: 8,
      docs: docLane > 0 ? docLane : 5,
      incoming: typeof summary.new_hires_month === 'number' ? summary.new_hires_month : 0,
    };
  }, [summary]);

  const dashComms = useMemo(() => {
    const critical = DEMO_NOTIFICATIONS.filter((n) => n.priority === 'high' && n.status !== 'completed').length;
    const tasksPending = DEMO_RRHH_TASKS.filter((x) => x.status === 'pending').length;
    const emailsPrepared = rrhhEmailLogs.filter((e) => e.status === 'prepared' || e.status === 'draft').length;
    const emailsRecent = rrhhEmailLogs.filter((e) => e.status === 'sent' || e.status === 'simulated').length;
    return { critical, tasksPending, emailsPrepared, emailsRecent };
  }, [rrhhEmailLogs]);

  const rrhhActions = useMemo(
    () => [
      { to: '/employee-lifecycle', labelKey: 'dashboard.rrhhActionLifecycle', icon: '↻' },
      { to: '/records', labelKey: 'dashboard.rrhhActionDocs', icon: '▣' },
      { to: '/leaves', labelKey: 'dashboard.rrhhActionLeaves', icon: '◷' },
      { to: '/expense-reports', labelKey: 'dashboard.rrhhActionExpenses', icon: '⧗' },
      { to: '/communications', labelKey: 'dashboard.rrhhActionComms', icon: '✉' },
      { to: '/attendance', labelKey: 'dashboard.rrhhActionAttendance', icon: '⏱' },
      { to: '/employee-lifecycle?tab=onboarding', labelKey: 'dashboard.rrhhActionOnboardComplete', icon: '◇' },
      { to: '/employee-lifecycle?tab=offboarding', labelKey: 'dashboard.rrhhActionOffboardBlocked', icon: '◈' },
    ],
    []
  );

  const dashProMetrics = useMemo(() => {
    if (!summary) return null;
    const climateNum = Number.isFinite(summary.average_climate_score) ? summary.average_climate_score : 0;
    const piS = piSnapshot?.score ?? summary.people_intelligence_score ?? 0;
    const total = summary.total_employees || 0;
    const present = summary.attendance_present_today || 0;
    const absenteeEst =
      total > 0
        ? Math.max(1, Math.min(18, 100 - Math.round((present / Math.max(total, 1)) * 100)))
        : 5;
    const climateScaled = climateNum > 0 ? Math.min(100, climateNum * 10) : 72;
    const orgHealthScore = Math.min(
      100,
      Math.round((Number(summary.compliance_rate ?? 100) + Number(piS) + climateScaled) / 3)
    );
    const trainGapPct = 72 + (Math.round(Number(piS)) % 23);
    const evalsDemo = 3 + ((summary.pending_leaves || 0) % 7);
    return { absenteeEst, orgHealthScore, trainGapPct, evalsDemo };
  }, [summary, piSnapshot]);

  const flowIaContext = useMemo(() => {
    if (!summary) return {};
    const climateNum = Number.isFinite(summary.average_climate_score) ? summary.average_climate_score : 0;
    const piS = piSnapshot?.score ?? summary.people_intelligence_score ?? 0;
    return {
      active_employees: summary.active_employees,
      total_employees: summary.total_employees,
      compliance_rate: summary.compliance_rate,
      pending_leaves: summary.pending_leaves,
      pending_expenses: summary.pending_expenses,
      people_intelligence_score: piS,
      org_health_score: dashProMetrics?.orgHealthScore,
      average_climate_score: climateNum,
      pending_expense_amount_cents: summary.pending_expense_amount_cents,
      documents_pending: summary.documents_pending,
      documents_expired: summary.documents_expired,
    };
  }, [summary, piSnapshot, dashProMetrics?.orgHealthScore]);
  useFlowIAScreenContext(flowIaContext);

  if (isEmployeeHubPersona(permSummary, authMe, isProductionUi)) {
    return <Navigate to="/employee-portal" replace />;
  }

  if (!summary) {
    return <div className="empty-state empty-state--premium">{t('common.loading')}</div>;
  }

  const eom = summary.employee_of_month || DEFAULT_SUMMARY.employee_of_month;
  const climate = Number.isFinite(summary.average_climate_score) ? summary.average_climate_score : 0;
  const scoreLabel =
    financeScore !== null && financeScore !== undefined ? `${Math.round(financeScore)}/100` : '—';

  const piScore = piSnapshot?.score ?? summary.people_intelligence_score ?? 0;
  const piRisk = piSnapshot?.risk_level ?? 'low';
  const piInsights = (piSnapshot?.insights || []).slice(0, 3);
  const piRecs = (piSnapshot?.recommendations || []).slice(0, 3);
  const rbacRolesCount = Array.isArray(rbacCatalog?.roles) ? rbacCatalog.roles.length : 6;

  return (
    <div className="page-stack page-stack--wide page-stack--dash-v15">
      <header className="dash-exec-header panel panel--elevated">
        <div className="dash-exec-header__main">
          <p className="dash-exec-header__eyebrow">{t('dashboard.rcEyebrow')}</p>
          <h1 className="dash-exec-header__title">{t('dashboard.title')}</h1>
          <p className="dash-exec-header__subtitle">{t('dashboard.subtitle')}</p>
          {!isProductionUi ? (
            <p className="dash-exec-header__rc-line panel__muted text-sm">{t('dashboard.rcHeaderLine')}</p>
          ) : (
            <p className="dash-exec-header__rc-line panel__muted text-sm">{t('dashboard.rcHeaderLineProd')}</p>
          )}
        </div>
        <div className="dash-exec-header__aside">
          <div className="dash-exec-header__period" aria-live="polite">
            <span className="dash-exec-header__period-label">{t('dashboard.periodLabel')}</span>
            <time className="dash-exec-header__period-value" dateTime={new Date().toISOString().slice(0, 10)}>
              {new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-AR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }).format(new Date())}
            </time>
          </div>
          {!isProductionUi ? (
            <>
              <span className="dash-pill dash-pill--demo">{t('dashboard.badgeCommercialDemo')}</span>
              <span className="dash-pill dash-pill--flow">{t('dashboard.badgeRC')}</span>
            </>
          ) : (
            <span className="dash-pill dash-pill--flow">{t('dashboard.badgeFlowIa')}</span>
          )}
        </div>
      </header>

      <section className="dash-exec-summary panel panel--elevated" aria-labelledby="dash-exec-summary-title">
        <div className="dash-section-head">
          <h2 id="dash-exec-summary-title" className="dash-section-title">
            {t('dashboard.execSummaryTitle')}
          </h2>
          <p className="panel__muted text-sm">{t('dashboard.execSummarySubtitle')}</p>
        </div>
        <div className="grid-stats dash-exec-summary__grid">
          <StatCard tone="accent" icon="◎" label={t('dashboard.execActive')} value={summary.active_employees ?? 0} />
          <StatCard
            icon="◎"
            label={t('dashboard.execCompliance')}
            value={`${Number(summary.compliance_rate ?? 100).toFixed(0)}%`}
            tone="success"
          />
          <StatCard
            icon="✦"
            label={t('dashboard.execClimate')}
            value={climate > 0 ? climate.toFixed(1) : '—'}
          />
          <StatCard icon="✓" label={t('dashboard.execAttendance')} value={summary.attendance_present_today ?? 0} />
          <StatCard
            icon="⏱"
            label={t('dashboard.execTasks')}
            value={dashComms.critical + dashComms.tasksPending}
            tone="warning"
          />
          <StatCard icon="⧉" label={t('dashboard.execPi')} value={`${piScore}/100`} />
        </div>
      </section>

      {dashProMetrics && (
        <section className="dash-pro-exec panel panel--elevated" aria-labelledby="dash-pro-org-title">
          <div className="dash-pro-exec__head">
            <div>
              <h2 id="dash-pro-org-title" className="dash-section-title">
                {t('dashboard.proOrgHealthTitle')}
              </h2>
              <p className="panel__muted text-sm">{t('dashboard.proOrgHealthLead')}</p>
            </div>
            <div className="dash-pro-org-health" aria-label={t('dashboard.proOrgHealthBadge')}>
              <span className="dash-pro-org-health__value">{dashProMetrics.orgHealthScore}</span>
              <span className="dash-pro-org-health__max">/100</span>
              <span className="dash-pill dash-pill--ok">{t('dashboard.proOrgHealthBadge')}</span>
            </div>
          </div>

          <div className="dash-section-head dash-pro-exec__kpi-head">
            <h3 className="dash-section-title dash-section-title--compact">{t('dashboard.proExecKpiTitle')}</h3>
            <p className="panel__muted text-sm">{t('dashboard.proExecKpiLead')}</p>
          </div>
          <div className="grid-stats dash-pro-exec__kpis">
            <StatCard icon="◎" label={t('dashboard.proHeadcount')} value={summary.active_employees ?? 0} tone="accent" />
            <StatCard
              icon="⏱"
              label={t('dashboard.proAbsentee')}
              value={`${dashProMetrics.absenteeEst}%`}
              hint={t('dashboard.proAbsenteeHint')}
            />
            <StatCard icon="◷" label={t('dashboard.proLeavesPen')} value={summary.pending_leaves ?? 0} tone="warning" />
            <StatCard icon="✦" label={t('dashboard.proClimateShort')} value={climate > 0 ? climate.toFixed(1) : '—'} />
            <StatCard icon="◇" label={t('dashboard.proEvalsPen')} value={dashProMetrics.evalsDemo} />
            <StatCard
              icon="▤"
              label={t('dashboard.proTrainGap')}
              value={`${dashProMetrics.trainGapPct}%`}
            />
            <StatCard
              icon="⧗"
              label={t('dashboard.proExpensesPen')}
              value={summary.pending_expenses ?? 0}
              hint={formatCents(summary.pending_expense_amount_cents ?? 0, 'ARS')}
              tone="accent"
            />
          </div>

          <div className="dash-pro-smart panel panel--muted">
            <h3 className="dash-section-title dash-section-title--compact">{t('dashboard.proSmartAlerts')}</h3>
            <ul className="dash-pro-smart__list">
              <li>{t('dashboard.proAlert1')}</li>
              <li>{t('dashboard.proAlert2')}</li>
              <li>{t('dashboard.proAlert3')}</li>
            </ul>
          </div>

          <div className="dash-pro-triple">
            <div className="panel panel--elevated dash-pro-triple__col">
              <h3 className="dash-section-title dash-section-title--compact">{t('dashboard.proNextExecTitle')}</h3>
              <ol className="dash-pro-triple__ol">
                <li>{t('dashboard.proNextExec1')}</li>
                <li>{t('dashboard.proNextExec2')}</li>
                <li>{t('dashboard.proNextExec3')}</li>
              </ol>
            </div>
            <div className="panel panel--elevated dash-pro-triple__col dash-pro-triple__col--risk">
              <h3 className="dash-section-title dash-section-title--compact">{t('dashboard.proRisksExecTitle')}</h3>
              <p className="panel__muted text-sm">{t('dashboard.proRisksExecLead')}</p>
              <ul className="dash-pro-triple__ul">
                <li>{t('dashboard.risk1')}</li>
                <li>{t('dashboard.risk3')}</li>
                <li>{t('dashboard.risk5')}</li>
              </ul>
            </div>
            <div className="panel panel--elevated dash-pro-triple__col dash-pro-triple__col--opp">
              <h3 className="dash-section-title dash-section-title--compact">{t('dashboard.proOppsExecTitle')}</h3>
              <p className="panel__muted text-sm">{t('dashboard.proOppsExecLead')}</p>
              <ul className="dash-pro-triple__ul">
                <li>{t('dashboard.recommended2')}</li>
                <li>{t('dashboard.recommended4')}</li>
                <li>{t('dashboard.insightSkills')}</li>
              </ul>
            </div>
          </div>
        </section>
      )}

      <p className="panel__muted text-sm dash-rc-notice">{t('rc.demoEnvironmentNotice')}</p>

      <section className="panel panel--elevated dash-module-state" aria-labelledby="dash-module-state-title">
        <h2 id="dash-module-state-title" className="dash-section-title">
          {t('dashboard.moduleStateTitle')}
        </h2>
        <dl className="settings-release-dl dash-module-state__dl">
          <div>
            <dt>{t('dashboard.moduleStateVersion')}</dt>
            <dd>{t('version')}</dd>
          </div>
          <div>
            <dt>{t('dashboard.moduleStateProduct')}</dt>
            <dd>{t('dashboard.moduleStateProductValue')}</dd>
          </div>
          <div>
            <dt>{t('dashboard.moduleStateMode')}</dt>
            <dd>{t('dashboard.moduleStateModeValue')}</dd>
          </div>
          <div>
            <dt>{t('dashboard.moduleStateEmail')}</dt>
            <dd>
              {!pubMail
                ? t('common.loading')
                : pubMail.mode === 'real' && pubMail.status === 'ready'
                  ? t('dashboard.moduleStateEmailReal')
                  : t('dashboard.moduleStateEmailDemo')}
            </dd>
          </div>
          <div>
            <dt>{t('dashboard.moduleStateStorage')}</dt>
            <dd>
              {!pubStorage
                ? t('common.loading')
                : pubStorage.status === 'ready'
                  ? t('dashboard.moduleStateStorageReady')
                  : pubStorage.status === 'metadata_only'
                    ? t('dashboard.moduleStateStorageMeta')
                    : t('dashboard.moduleStateStoragePending')}
            </dd>
          </div>
          <div>
            <dt>{t('dashboard.moduleStateFlow')}</dt>
            <dd>{t('dashboard.moduleStateFlowValue')}</dd>
          </div>
          <div>
            <dt>{t('dashboard.moduleStateFinance')}</dt>
            <dd>{t('dashboard.moduleStateFinanceValue')}</dd>
          </div>
        </dl>
        <Link to="/settings?tab=release" className="btn btn--sm btn--secondary">
          {t('dashboard.moduleStateCta')}
        </Link>
      </section>

      <section className="panel panel--elevated dash-ops-hr" aria-labelledby="dash-ops-hr-title">
        <div className="dash-section-head">
          <h2 id="dash-ops-hr-title" className="dash-section-title">
            {t('dashboard.opsHrTitle')}
          </h2>
          <p className="panel__muted text-sm">{t('dashboard.opsHrSubtitle')}</p>
        </div>
        <div className="dash-ops-hr-grid">
          {OPS_HR_STATUS.map((m) => (
            <Link key={m.to} to={m.to} className="dash-ops-hr-card">
              <span>{t(m.labelKey)}</span>
              <span className="dash-pill dash-pill--muted">{t('dashboard.opsHrLink')}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="panel panel--elevated dash-demo-ready" aria-labelledby="dash-demo-ready-title">
        <h2 id="dash-demo-ready-title" className="dash-section-title">
          {t('dashboard.demoReadyTitle')}
        </h2>
        <p className="panel__muted text-sm">{t('dashboard.demoReadySubtitle')}</p>
        <ul className="dash-rc-checklist">
          {DEMO_READY_KEYS.map((k) => (
            <li key={k}>{t(`dashboard.${k}`)}</li>
          ))}
        </ul>
      </section>

      <section className="panel panel--elevated dash-next-real" aria-labelledby="dash-next-real-title">
        <h2 id="dash-next-real-title" className="dash-section-title">
          {t('dashboard.nextRealTitle')}
        </h2>
        <ol className="dash-next-real-list">
          {NEXT_REAL_KEYS.map((k) => (
            <li key={k}>{t(`dashboard.${k}`)}</li>
          ))}
        </ol>
      </section>

      <section className="dash-priority-split" aria-label={t('dashboard.risksTitle')}>
        <div className="panel panel--elevated dash-priority-split__col">
          <h2 className="dash-section-title">{t('dashboard.risksTitle')}</h2>
          <p className="panel__muted text-sm">{t('dashboard.risksBody')}</p>
          <ul className="dash-risks-list">
            <li>{t('dashboard.risk1')}</li>
            <li>{t('dashboard.risk2')}</li>
            <li>{t('dashboard.risk3')}</li>
            <li>{t('dashboard.risk4')}</li>
            <li>{t('dashboard.risk5')}</li>
          </ul>
        </div>
        <div className="panel panel--elevated dash-priority-split__col">
          <h2 className="dash-section-title">{t('dashboard.recommendedTitle')}</h2>
          <ol className="dash-recommended-list">
            <li>{t('dashboard.recommended1')}</li>
            <li>{t('dashboard.recommended2')}</li>
            <li>{t('dashboard.recommended3')}</li>
            <li>{t('dashboard.recommended4')}</li>
            <li>{t('dashboard.recommended5')}</li>
          </ol>
        </div>
      </section>

      <section className="dash-kpi-section panel panel--elevated">
        <h2 className="dash-section-title">{t('dashboard.kpiGridTitle')}</h2>
        <div className="grid-stats dash-kpi-grid">
          <StatCard icon="◉" label={t('dashboard.kpiTotalEmployees')} value={summary.total_employees ?? 0} />
          <StatCard tone="accent" icon="◎" label={t('dashboard.kpiActiveShort')} value={summary.active_employees ?? 0} />
          <StatCard icon="◷" label={t('dashboard.pendingLeaves')} value={summary.pending_leaves ?? 0} />
          <StatCard
            icon="⧗"
            label={t('dashboard.pendingExpenses')}
            value={summary.pending_expenses ?? 0}
            hint={formatCents(summary.pending_expense_amount_cents ?? 0, 'ARS')}
            tone="warning"
          />
          <StatCard
            icon="▣"
            label={t('dashboard.kpiDocsPair')}
            value={`${summary.documents_pending ?? 0} / ${summary.documents_expired ?? 0}`}
          />
          <StatCard icon="✓" label={t('dashboard.kpiPresentToday')} value={summary.attendance_present_today ?? 0} />
          <StatCard icon="◎" label={t('dashboard.docKpiCompliance')} value={`${Number(summary.compliance_rate ?? 100).toFixed(0)}%`} tone="success" />
          <StatCard icon="◇" label={t('dashboard.kpiDocMissingFile')} value={summary.documents_missing_file ?? 0} tone="accent" />
          <StatCard icon="▤" label={t('dashboard.kpiPayslipsAvail')} value={summary.payslips_available ?? 0} />
          <StatCard icon="◈" label={t('dashboard.kpiPayslipsDraft')} value={summary.payslips_draft ?? 0} tone="warning" />
          <StatCard icon="⎔" label={t('dashboard.kpiStorageRuntime')} value={t(`dashboard.storageStatus_${summary.storage_status || 'metadata_only'}`)} />
          <StatCard icon="⧉" label={t('dashboard.kpiPiScore')} value={`${piScore}/100`} />
        </div>
      </section>

      <section className="dash-security-mini panel panel--elevated" aria-labelledby="dash-security-title">
        <div className="dash-security-mini__head">
          <div>
            <h2 id="dash-security-title" className="dash-section-title dash-section-title--compact">
              {t('dashboard.securityCardTitle')}
            </h2>
            <p className="panel__muted text-sm">{t('dashboard.securityCardSubtitle')}</p>
          </div>
          <Link to="/settings?tab=roles" className="btn btn--sm btn--secondary">
            {t('dashboard.securityCta')}
          </Link>
        </div>
        <div className="dash-security-mini__grid">
          <div className="dash-security-mini__cell">
            <span className="dash-security-mini__label">{t('dashboard.securityRolesLabel')}</span>
            <strong className="dash-security-mini__value">{rbacRolesCount}</strong>
            <span className="dash-security-mini__hint">{t('dashboard.securityRolesValue')}</span>
          </div>
          <div className="dash-security-mini__cell">
            <span className="dash-security-mini__label">{t('dashboard.securityPermsLabel')}</span>
            <strong className="dash-security-mini__value">
              {permSummary?.permissions ? `${Object.keys(permSummary.permissions).length}` : '—'}
            </strong>
            <span className="dash-security-mini__hint">{t('dashboard.securityPermsValue')}</span>
          </div>
          <div className="dash-security-mini__cell">
            <span className="dash-security-mini__label">{t('dashboard.securityFlowLabel')}</span>
            <strong className="dash-security-mini__value dash-security-mini__value--pending">
              {t('dashboard.securityFlowStatus')}
            </strong>
            <span className="dash-security-mini__hint">{t('dashboard.securityFlowValue')}</span>
          </div>
          <div className="dash-security-mini__cell">
            <span className="dash-security-mini__label">{t('dashboard.securityRiskLabel')}</span>
            <strong className="dash-security-mini__value dash-security-mini__value--warn">
              {t('dashboard.securityRiskBadge')}
            </strong>
            <span className="dash-security-mini__hint">{t('dashboard.securityRiskValue')}</span>
          </div>
        </div>
      </section>

      {peopleOpsStats && (
        <section className="dash-people-ops panel panel--elevated">
          <div className="dash-section-head">
            <h2 className="dash-section-title">{t('dashboard.peopleOpsTitle')}</h2>
            <p className="panel__muted text-sm">{t('dashboard.peopleOpsSubtitle')}</p>
          </div>
          <div className="grid-stats dash-people-ops__kpis">
            <StatCard icon="◇" label={t('dashboard.peopleKpiOnboard')} value={peopleOpsStats.onboard} tone="accent" />
            <StatCard icon="◈" label={t('dashboard.peopleKpiOffboard')} value={peopleOpsStats.offboard} />
            <StatCard icon="⏱" label={t('dashboard.peopleKpiHrTasks')} value={peopleOpsStats.hrTasks} tone="warning" />
            <StatCard icon="▣" label={t('dashboard.peopleKpiDocs')} value={peopleOpsStats.docs} />
            <StatCard icon="◎" label={t('dashboard.peopleKpiIncoming')} value={peopleOpsStats.incoming} tone="success" />
          </div>
        </section>
      )}

      <section className="dash-comms panel panel--elevated">
        <div className="dash-section-head dash-section-head--row">
          <div>
            <h2 className="dash-section-title">{t('dashboard.commsPanelTitle')}</h2>
            <p className="panel__muted text-sm">{t('dashboard.commsPanelSubtitle')}</p>
          </div>
          <span className="dash-pill dash-pill--ok">{t('dashboard.commsInternalInd')}</span>
        </div>
        <div className="dash-comms-kpis grid-stats">
          <StatCard icon="!" label={t('dashboard.commsCritical')} value={dashComms.critical} tone="warning" />
          <StatCard icon="⏱" label={t('dashboard.commsTasks')} value={dashComms.tasksPending} />
          <StatCard icon="✉" label={t('dashboard.commsEmailsPrepared')} value={dashComms.emailsPrepared} tone="accent" />
          <StatCard icon="◎" label={t('dashboard.commsEmailsRecent')} value={dashComms.emailsRecent} />
        </div>
        <div className="dash-comms-split">
          <div>
            <h3 className="dash-comms-sub">{t('dashboard.commsRecentBulletins')}</h3>
            {recentComms.length === 0 ? (
              <p className="panel__muted text-sm">{t('dashboard.activityEmpty')}</p>
            ) : (
              <ul className="mini-list">
                {recentComms.slice(0, 3).map((c) => (
                  <li key={c.id} className="mini-list__row">
                    <span className="cell-truncate">{c.title}</span>
                    <Badge variant="approved">{t('communications.status.published')}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="dash-comms-sub">{t('dashboard.commsNextActions')}</h3>
            <ol className="dash-comms-next-list">
              <li>{t('dashboard.commsNext1')}</li>
              <li>{t('dashboard.commsNext2')}</li>
              <li>{t('dashboard.commsNext3')}</li>
            </ol>
          </div>
        </div>
        {rrhhEmailLogs.length > 0 && (
          <div className="dash-comms-email-log">
            <h3 className="dash-comms-sub">{t('dashboard.commsEmailLogTitle')}</h3>
            <ul className="mini-list">
              {rrhhEmailLogs.map((row) => (
                <li key={row.id} className="mini-list__row">
                  <span className="cell-truncate">
                    {row.subject} · {row.to_email}
                  </span>
                  <Badge variant={row.status === 'sent' ? 'approved' : row.status === 'failed' ? 'rejected' : 'pending'}>
                    {t(`communications.emailLogStatus.${row.status}`)}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="dash-comms-links">
          <Link to="/notifications" className="btn btn--secondary btn--sm">
            {t('dashboard.commsLinkNotifications')}
          </Link>
          <Link to="/communications" className="btn btn--secondary btn--sm">
            {t('dashboard.commsLinkCommunications')}
          </Link>
        </div>
      </section>

      <section className="dash-actions-section">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('dashboard.rrhhNextTitle')}</h2>
          <p className="panel__muted text-sm">{t('dashboard.rrhhActionsSubtitle')}</p>
        </div>
        <div className="dash-action-cards">
          {rrhhActions.map((a) => (
            <Link key={a.to} to={a.to} className="dash-action-card">
              <span className="dash-action-card__icon" aria-hidden>
                {a.icon}
              </span>
              <span className="dash-action-card__label">{t(a.labelKey)}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="dash-suite panel panel--elevated">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('dashboard.suiteTitle')}</h2>
          <p className="panel__muted text-sm">{t('dashboard.suiteSubtitle')}</p>
        </div>
        <div className="dash-suite-grid">
          {[
            { to: '/employee-portal', labelKey: 'dashboard.suitePortal' },
            { to: '/employee-lifecycle', labelKey: 'dashboard.suiteLifecycle' },
            { to: '/records', labelKey: 'dashboard.suiteLegajo' },
            { to: '/attendance', labelKey: 'dashboard.suiteAttendance' },
            { to: '/leaves', labelKey: 'dashboard.suiteLeaves' },
            { to: '/expense-reports', labelKey: 'dashboard.suiteExpenses' },
            { to: '/communications', labelKey: 'dashboard.suiteComms' },
            { to: '/performance', labelKey: 'dashboard.suiteEvaluations' },
            { to: '/training', labelKey: 'dashboard.suiteTraining' },
            { to: '/surveys', labelKey: 'dashboard.suiteClimate' },
            { to: '/personal-finance', labelKey: 'dashboard.suitePF' },
            { to: '/people-intelligence', labelKey: 'dashboard.suitePI' },
            { to: '/reports', labelKey: 'dashboard.suiteReports' },
          ].map((m) => (
            <Link key={m.to} to={m.to} className="dash-suite-card">
              <span className="dash-suite-card__label">{t(m.labelKey)}</span>
              <span className="dash-pill dash-pill--ok">{t('dashboard.suiteStatusActive')}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="dash-pi-snapshot panel panel--elevated">
        <div className="dash-section-head dash-section-head--row">
          <h2 className="dash-section-title">{t('dashboard.piSnapshotTitle')}</h2>
          <span className="dash-pill dash-pill--muted">{t('dashboard.piSnapshotRules')}</span>
        </div>
        <p className="panel__muted text-sm">{t('dashboard.piSnapshotNext')}</p>
        <div className="dash-pi-snapshot__grid">
          <div className="dash-pi-score-block">
            <span className="dash-pi-score-block__value">{piScore}</span>
            <span className="dash-pi-score-block__label">/100</span>
            <div className={`dash-pi-risk ${riskTone(piRisk)}`}>
              {piRisk === 'high' ? t('pi.riskHigh') : piRisk === 'medium' ? t('pi.riskMedium') : t('pi.riskLow')}
            </div>
          </div>
          <div className="dash-pi-columns">
            <div>
              <h3 className="dash-pi-subtitle">{t('pi.insightsTitle')}</h3>
              <ul className="dash-pi-list">
                {(piInsights.length ? piInsights : [t('dashboard.activityEmpty')]).map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="dash-pi-subtitle">{t('pi.recTitle')}</h3>
              <ul className="dash-pi-list">
                {(piRecs.length ? piRecs : [t('pi.recEmpty')]).map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="dash-activity panel panel--elevated">
        <div className="dash-section-head">
          <h2 className="dash-section-title">{t('dashboard.activitySectionTitle')}</h2>
          <p className="panel__muted text-sm">{t('dashboard.activityDemoHint')}</p>
        </div>
        <div className="dash-activity-grid">
          <div className="dash-activity-col">
            <h3 className="dash-activity-col__title">{t('dashboard.activityColDocs')}</h3>
            {recentDocs.length === 0 ? (
              <p className="panel__muted text-sm">{t('dashboard.activityEmpty')}</p>
            ) : (
              <ul className="mini-list">
                {recentDocs.map((d) => (
                  <li key={d.id} className="mini-list__row">
                    <span className="cell-truncate">{d.title}</span>
                    <Badge variant={hrDocBadgeVariant(d.status)}>{d.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="dash-activity-col">
            <h3 className="dash-activity-col__title">{t('dashboard.activityColComms')}</h3>
            {recentComms.length === 0 ? (
              <p className="panel__muted text-sm">{t('dashboard.activityEmpty')}</p>
            ) : (
              <ul className="mini-list">
                {recentComms.map((c) => (
                  <li key={c.id} className="mini-list__row">
                    <span className="cell-truncate">{c.title}</span>
                    <Badge variant="approved">{t('communications.status.published')}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="dash-activity-col">
            <h3 className="dash-activity-col__title">{t('dashboard.activityColLeaves')}</h3>
            {leaves.length === 0 ? (
              <p className="panel__muted text-sm">{t('dashboard.noLeaves')}</p>
            ) : (
              <ul className="mini-list">
                {leaves.slice(0, 4).map((lv) => (
                  <li key={lv.id} className="mini-list__row">
                    <div>
                      <strong className="cell-truncate">{lv.employee_name ?? '—'}</strong>
                      <div className="text-muted text-sm">
                        {formatDate(lv.start_date)} → {formatDate(lv.end_date)}
                      </div>
                    </div>
                    <Badge variant={lv.status || 'pending'}>{t(`status.${lv.status}`) || lv.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="dash-activity-col">
            <h3 className="dash-activity-col__title">{t('dashboard.activityColExpenses')}</h3>
            {recentExpenses.length === 0 ? (
              <p className="panel__muted text-sm">{t('dashboard.noExpenses')}</p>
            ) : (
              <ul className="mini-list">
                {recentExpenses.map((ex) => (
                  <li key={ex.id} className="mini-list__row">
                    <div>
                      <strong className="cell-truncate">{ex.employee_name ?? '—'}</strong>
                      <div className="text-muted text-sm">{ex.category ?? '—'}</div>
                    </div>
                    <div className="mini-list__right">
                      <span>{formatCents(ex.amount_cents ?? 0, ex.currency || 'USD')}</span>
                      <Badge variant={ex.status === 'paid' ? 'paid' : 'pending'}>{t(`status.${ex.status}`) || ex.status}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <div className="grid-2 grid-2--priority">
        <section className="panel panel--elevated panel--eom-spotlight">
          <h2 className="panel__title">{t('dashboard.employeeMonth')}</h2>
          {eom?.name ? (
            <>
              <p className="eom-spotlight__name">{eom.name}</p>
              <p className="panel__muted">{eom.area || ''}</p>
            </>
          ) : (
            <p className="panel__muted">{t('dashboard.noEom')}</p>
          )}
        </section>
        <section className="panel panel--elevated">
          <h2 className="panel__title">{t('dashboard.climate')}</h2>
          <p className="dash-climate-value">{climate.toFixed(1)}/10</p>
          <p className="panel__muted text-sm">{t('dashboard.avgFinancialScore')}: {scoreLabel}</p>
          <p className="panel__muted text-sm">{t('dashboard.kpiExpensesApprove')}: {pendingApproval}</p>
          <p className="panel__muted text-sm dash-fin-wellbeing-note">{t('dashboard.finWellbeingAggregatedNote')}</p>
        </section>
      </div>

      <div className="grid-2">
        <section className="panel">
          <h2 className="panel__title">{t('dashboard.recentAwards')}</h2>
          {awards.length === 0 ? (
            <div className="empty-inline">{t('dashboard.noAwards')}</div>
          ) : (
            <ul className="mini-list">
              {awards.map((aw) => (
                <li key={aw.id} className="mini-list__row">
                  <div>
                    <strong>{aw.title ?? '—'}</strong>
                    <div className="text-muted text-sm">{aw.employee_name ?? '—'}</div>
                  </div>
                  <span className="points-pill">+{aw.points ?? 0}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="panel panel--accent">
          <h2 className="panel__title">{t('dashboard.insights')}</h2>
          <ul className="insights-list">
            <li>{t('dashboard.insightRetention')}</li>
            <li>{t('dashboard.insightSkills')}</li>
          </ul>
          <div className="dashboard-hero__cta-row" style={{ marginTop: '1rem' }}>
            <Link to="/employees" className="btn btn--secondary btn--sm">
              {t('dashboard.ctaEmployees')}
            </Link>
            <Link to="/expense-reports" className="btn btn--secondary btn--sm">
              {t('dashboard.ctaExpenses')}
            </Link>
          </div>
        </section>
      </div>

      <section className="panel panel--strategic">
        <div className="panel__headrow">
          <h2 className="panel__title panel__title--flush">{t('dashboard.strategicModules')}</h2>
          <p className="panel__muted panel__muted--inline">{t('dashboard.strategicHint')}</p>
        </div>
        <div className="module-card-grid module-card-grid--six">
          {STRATEGIC_MODULES.map((m) => (
            <ModuleCard key={m.to} to={m.to} title={t(m.titleKey)} description={t(m.descKey)} icon={m.icon} status={m.status} />
          ))}
        </div>
      </section>
    </div>
  );
}
